import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import {
  Box,
  Chip,
  Typography,
} from '@mui/material'
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded'
import { Container } from '@mui/system'
import { selectCompanies } from '../../redux/features/companies/companiesSlice'
import { retrieveCompanyDetails, ssoGetCompanyConfig } from '../../redux/features/companies/companiesThunk'
import { companyButtonList } from '../../components/utils/buttonList'
import UseTranslation from '../../hooks/UseTranslation.jsx'
import { SAML_KEY } from '../../utils/constants'
import { ReactComponent as SingleCompanyIcon } from '../../images/SingleCompany.svg'
import TitleWithBackBtn from '../../components/PageHelpers/TitleWithBackBtn.jsx'
import OfoqThemeBtn from '../../components/Buttons/OfoqThemeBtn.jsx'
import AppRoutes from '../../services/AppRoutes'
import SvgIconSize from '../../components/Cards/helpers/SvgIconSize.jsx'
import { ReactComponent as EditIcon } from '../../images/Edit.svg'
import GeneralDetails from './GeneralDetails'
import SsoConfiguration from './SsoConfiguration'
import ConsentForm from './ConsentForm'
import { isNotEmpty } from '../../utils/helpers'

const statusForCompany = (company) => {
  if (!company) return null
  const isActive = company.active
  const isSuspended = company.suspended

  if (isActive && !isSuspended) return companyButtonList[0]
  if (!isActive && !isSuspended) return companyButtonList[1]
  if (!isActive && isSuspended) return companyButtonList[2]
  return null
}

const Index = () => {
  const { companyId } = useParams()
  const history = useHistory()
  const dispatch = useDispatch()
  const { t } = UseTranslation()
  const companies = useSelector(selectCompanies)
  const refreshAttempted = useRef(false)
  const companyDetails = companies?.companyDetails
  const companyPolicies = companyDetails?.company_policies
  useEffect(() => {
    refreshAttempted.current = false
  }, [companyId])

  useEffect(() => {
    if (!refreshAttempted.current) {
      refreshAttempted.current = true
      dispatch(retrieveCompanyDetails({ id: companyId }))
    }
  }, [companyId, dispatch])

  useEffect(() => {
    if (!companyDetails?.id) return
    const usesSaml = companyDetails?.sso_protocol === SAML_KEY
    if (!usesSaml) return
    dispatch(ssoGetCompanyConfig({ id: companyDetails?.id }))
  }, [companyDetails?.id, companyDetails?.sso_protocol, dispatch])

  const status = statusForCompany(companyDetails)
  const samlCompany = companyDetails?.sso_protocol === SAML_KEY

  let ssoChipLabel = t?.['SSO Disabled']
  if (samlCompany) ssoChipLabel = t?.['SSO Active']

  const isCompanyActive = status?.title === 'Active'
  const companyStatusDotColor = isCompanyActive
    ? '#29CC8B'
    : (status?.color ?? companyButtonList[1].color)
  const companyStatusChipSx = styles.companyStatusChip(
    isCompanyActive,
    companyStatusDotColor,
  )

  const openEdit = () => {
    if (!companyDetails?.id) return
    const tenant = companyDetails?.tenant_list?.[0] || null
    const companyRow = {
      company: companyDetails,
      tenant,
      integration: tenant?.integration || null,
    }
    history.push({
      pathname: AppRoutes.editCompany.replace(':companyId', String(companyDetails.id)),
      state: { companyRow },
    })
  }
  const goCompaniesList = () => {
    history.push(AppRoutes.home)
  }

  let ssoAccessLabel = t?.SsoNotConfigured
  let ssoAccessPositive = false
  if (samlCompany) {
    ssoAccessLabel = t?.SsoSamlActiveLabel
    ssoAccessPositive = true
  }

  return (
     <Container maxWidth="xl" sx={styles.container}>
       <TitleWithBackBtn
        title={
          <Box sx={styles.titleBlock}>
            <Box sx={styles.titleWithIcon}>
            <SvgIconSize icon={<SingleCompanyIcon />} size="xl" customStyle={styles.companyIcon} />
            <Typography variant='h5' sx={styles.companyTitle}>{companyDetails?.name}</Typography>
          </Box>
          <Box sx={styles.badges}>
            <Chip
              size="small"
              icon={(
                <FiberManualRecordRoundedIcon
                  sx={styles.statusDotIcon(samlCompany ? '#29CC8B' : '#92929d')}
                />
              )}
              label={ssoChipLabel}
              sx={samlCompany ? styles.ssoChipActive : styles.ssoChipDisabled}
            />
            <Chip
              size="small"
              icon={(
                <FiberManualRecordRoundedIcon
                  sx={styles.statusDotIcon(isCompanyActive ? '#29CC8B' : '#92929d')}
                />
              )}
              label={t?.[status?.title]}
              sx={companyStatusChipSx}
            />
        </Box>
          </Box>
        }
        backBtnTxt={t?.['Back to Companies']}
        onBack={goCompaniesList}
        customStyle={styles.header}
      >
          <Box sx={styles.btnContainer}>
        <OfoqThemeBtn
          name={t?.['Edit Company']}
          customStyle={styles.editBtn}
          startIcon={<SvgIconSize icon={<EditIcon />} size="sm" />}
          onClick={openEdit}
          />
          </Box>
      </TitleWithBackBtn>

      <Container maxWidth="md" sx={styles.mainContainerStack}>
        <GeneralDetails
          companyDetails={companyDetails}
          ssoAccessLabel={ssoAccessLabel}
          ssoAccessPositive={ssoAccessPositive}
        />

        {samlCompany && (
          <SsoConfiguration
            companyDetails={companyDetails}
            samlCompany={samlCompany}
          />
        )}

        {isNotEmpty(companyPolicies) && (
          <ConsentForm companyPolicies={companyPolicies} />
        )}
      </Container>
    </Container>
  )
}

const styles = {
  statusDotIcon: (color) => ({
    '&&': {
      color: `${color} !important`,
      fontSize: 14,
    },
  }),
  companyStatusChip: (isCompanyActive, companyStatusDotColor) => (
    isCompanyActive
      ? {
        backgroundColor: 'rgba(41, 204, 139, 0.12)',
        color: '#1a8f5f',
        fontWeight: 600,
        border: 'none',
        '& .MuiChip-icon': { marginLeft: '8px', color: '#29CC8B !important' },
      }
      : {
        backgroundColor: '#92929d',
        color: '#ffffff',
        fontWeight: 600,
        border: 'none',
        '& .MuiChip-icon': { marginLeft: '8px', color: companyStatusDotColor },
      }
  ),
  container: {
    padding: { xs: '0px', sm: '0px 25px 0px 25px', md: ' 0px 52px !important' },
  },
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 30,
    backgroundColor: 'background.paper',
    height: { xs: 'auto', sm: '90px' },
    padding: { xs: '0px 0px 10px', sm: '0px 0px 10px' },
    borderBottomLeftRadius: '15px',
    borderBottomRightRadius: '15px',
    margin: { xs: '-10px 10px 10px', sm: '-29px 0px 10px 0px', md: '-30px 0px 10px 0px' },
    '& > div': {
      marginTop: { xs: '5px', sm: '0px' },
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'column' },
      alignItems: { xs: 'flex-start', sm: 'unset' },
      px: { xs: '10px', sm: 'unset' },
      width: { xs: '100%', sm: 'unset' },

    },
    '& .container': {
      width: { xs: '100%', sm: 'unset' },
    },
    '& .mainTitle': {
      width: { xs: '95%', sm: 'unset' },
    },
    '& .backBtn': {
      minWidth: '20px',
      m: { xs: '0 5px 0 0', sm: '5px 0' },
    },
  },
  titleWithIcon: {
    ml: { xs: '-20px', sm: '10px' },
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: { xs: '16px', sm: '24px' },
    '& svg': {
      width: { xs: '20px', sm: '35px' },
      height: { xs: '20px', sm: '35px' },
      paddingRight: '5px',
    },
  },
  titleBlock: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    justifyContent: { xs: 'space-between', sm: 'unset' },
    gap: { xs: 1, sm: 2 },
    flex: 1,
    minWidth: 0,
    marginLeft: { xs: '0px', sm: '0px' },
    marginTop: { xs: '10px', sm: '0px' },
    width: { xs: '95%', sm: 'unset' },
  },
  companyTitle: {
    fontSize: { xs: '16px', sm: '24px' },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: { xs: '150px', sm: '500px' },
  },
  badges: {
    display: 'flex',
    justifyContent: { xs: 'flex-start', sm: 'unset' },
    flexWrap: { xs: 'wrap', sm: 'unset' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    gap: { xs: 1, sm: 1 },
    width: { xs: '100%', sm: 'unset' },
  },
  ssoChipActive: {
    backgroundColor: 'rgba(41, 204, 139, 0.12)',
    color: '#1a8f5f',
    fontWeight: 600,
    border: 'none',
    '& .MuiChip-icon': { marginLeft: '8px' },
  },
  ssoChipDisabled: {
    backgroundColor: 'rgba(146, 146, 157, 0.12)',
    color: '#696974',
    fontWeight: 600,
    border: 'none',
    '& .MuiChip-icon': { marginLeft: '8px' },
  },
  mainContainerStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    mx: { xs: '0px', sm: 'auto' },
    marginBottom: { xs: '50px', sm: '0px' },
  },
  btnContainer: {
    position: { xs: 'fixed', sm: 'unset' },
    bottom: 0,
    zIndex: 100,
    right: 0,
    width: { xs: '97%', sm: 'fit-content' },
    border: { xs: '1px solid grey.200', sm: 'none' },
    backgroundColor: 'background.paper',
    padding: '10px',
    marginRight: { xs: '0px', sm: '0px' },
  },
  editBtn: {
    boxSizing: 'border-box',
    width: { xs: '100%', sm: '100%' },
    maxWidth: { xs: '100%', sm: 'fit-content' },
    height: { xs: '32px', sm: '40px' },
    color: 'text.main',
    textTransform: 'none',
    mx: { xs: '0px', sm: '0px' },
    padding: { xs: '0px', sm: '15px' },
    marginRight: { xs: '0px !important', sm: '20px !important' },
    '& p': {
      fontSize: { xs: '12px', sm: '14px' },
      maxWidth: { xs: '196px', sm: '190px' },
      paddingLeft: { xs: '0px', sm: '10px' },
    },
    '&:hover': {
      backgroundColor: 'primary.main',
    },
  },
  companyIcon: {
    borderRadius: '15px',
    padding: '10px',
    border: '1px solid rgba(29, 158, 117, 0.20)',
    backgroundColor: 'rgba(29, 158, 117, 0.12)',
    '& svg': {
      paddingRight: '0px',
    },
  },
}

export default Index
